import { AdocEntryList } from '@/components/features/Adoc/AdocEntryList';
import { AdocPreviewDialog } from '@/components/features/Adoc/AdocPreviewDialog';
import { AdocSignaturePanel } from '@/components/features/Adoc/AdocSignaturePanel';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import IndexLayout from '@/layouts/index-layout';
import { formatBytes, formatDateTime } from '@/lib/adoc/format';
import { type BreadcrumbItem, type SharedData } from '@/types';
import type { AdocEntrySummary, AdocFileSummary, AdocSignature } from '@/types/adoc';
import { Head, router, usePage } from '@inertiajs/react';
import { ArrowLeft, Download, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { route } from 'ziggy-js';

interface AdocShowProps {
    file: AdocFileSummary;
    entries: AdocEntrySummary[];
    signatures: AdocSignature[];
    authors: string[];
    standard_version: string | null;
    generator: string | null;
}

export default function AdocShow({ file, entries, signatures, authors, standard_version, generator }: AdocShowProps) {
    const { t } = useTranslations();
    const { auth } = usePage<SharedData>().props;
    const canDelete = (auth?.abilities ?? []).includes('adoc.delete');

    const [preview, setPreview] = useState<AdocEntrySummary | null>(null);

    const title = file.doc_title ?? file.original_name;

    const breadcrumbs: BreadcrumbItem[] = [
        { title: t('adoc.index.title', 'ADOC documents'), href: '/adoc' },
        { title, href: route('adoc.show', file.id) },
    ];

    const destroy = (): void => {
        if (window.confirm(t('adoc.index.delete_confirm', 'Delete this container? This cannot be undone.'))) {
            router.delete(route('adoc.destroy', file.id));
        }
    };

    const refresh = (): void => {
        router.post(route('adoc.refresh', file.id), {}, { preserveScroll: true });
    };

    const actions = (
        <>
            <Button variant="outline" onClick={() => router.visit(route('adoc.index'))}>
                <ArrowLeft />
                {t('adoc.show.back', 'Back to the list')}
            </Button>
            <Button asChild variant="outline">
                <a href={route('adoc.download', file.id)}>
                    <Download />
                    {t('adoc.show.download', 'Download original')}
                </a>
            </Button>
            <Button variant="outline" onClick={refresh}>
                <RefreshCw />
                {t('adoc.show.refresh', 'Keep 30 more days')}
            </Button>
            {canDelete ? (
                <Button variant="outline" onClick={destroy}>
                    <Trash2 />
                    {t('adoc.show.delete', 'Delete')}
                </Button>
            ) : null}
        </>
    );

    const meta: Array<{ label: string; value: string }> = [
        { label: t('adoc.show.meta.authors', 'Author'), value: authors.join(', ') || '—' },
        { label: t('adoc.show.meta.created', 'Created'), value: formatDateTime(file.doc_created_at) },
        { label: t('adoc.show.meta.size', 'Container size'), value: formatBytes(file.file_size) },
        { label: t('adoc.show.meta.standard', 'Standard'), value: standard_version ?? '—' },
        { label: t('adoc.show.meta.generator', 'Created with'), value: generator ?? '—' },
    ];

    return (
        <>
            <Head title={title} />
            <IndexLayout breadcrumbs={breadcrumbs} title={title} subtitle={file.original_name} actions={actions}>
                <div className="bg-card grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl border px-4 py-3 sm:grid-cols-3 lg:grid-cols-5">
                    {meta.map((item) => (
                        <div key={item.label} className="min-w-0">
                            <div className="text-muted-foreground text-xs">{item.label}</div>
                            <div className="truncate text-sm font-medium" title={item.value}>
                                {item.value}
                            </div>
                        </div>
                    ))}
                </div>

                <div>
                    <h2 className="mb-2 font-semibold">{t('adoc.show.documents.title', 'Documents in this container')}</h2>
                    <AdocEntryList fileId={file.id} entries={entries} onPreview={setPreview} />
                </div>

                <AdocSignaturePanel signatures={signatures} />

                <AdocPreviewDialog fileId={file.id} entry={preview} onClose={() => setPreview(null)} />
            </IndexLayout>
        </>
    );
}
